/**
 * Issue Registry — central catalog of all known operational issue codes.
 *
 * Centralized + granular: one file per provider, merged here into a single
 * source of truth. Add a code by editing its provider's `*.registry.ts`; the
 * merge and `IssueCode` union update automatically.
 *
 * This is NOT Sentry (code bugs). It tracks operational health: provider
 * failures, external API conflicts, webhook delivery, telemetry pipelines.
 *
 * @example
 * import { ISSUE_REGISTRY, type IssueCode } from "@nexus/types";
 * const def = ISSUE_REGISTRY["WHATSAPP_SENDER_WABA_CONFLICT"];
 * // def.category === "provider"; def.severity === "high"
 */
import type { IssueDefinition } from "../issue-definition";
import { BILLING_ISSUES } from "./billing.registry";
import { CAPABILITY_ISSUES } from "./capability.registry";
import { CLERK_ISSUES } from "./clerk.registry";
import { CLERK_EVENT_OBSERVATION_ISSUES } from "./clerk-event-observation.registry";
import { EMBED_ISSUES } from "./embed.registry";
import { EXTERNAL_API_ISSUES } from "./external-api.registry";
import { GOOGLE_ISSUES } from "./google.registry";
import { INSTAGRAM_ISSUES } from "./instagram.registry";
import { MESSENGER_ISSUES } from "./messenger.registry";
import { OAUTH_ISSUES } from "./oauth.registry";
import { OUTLOOK_ISSUES } from "./outlook.registry";
import { POST_MERGE_CI_ISSUES } from "./post-merge-ci.registry";
import { PROVIDER_SPEND_ISSUES } from "./provider-spend.registry";
import { SANDBOX_ISSUES } from "./sandbox.registry";
import { SLACK_ISSUES } from "./slack.registry";
import { TEAMS_ISSUES } from "./teams.registry";
import { TELEGRAM_ISSUES } from "./telegram.registry";
import { TWILIO_ISSUES } from "./twilio.registry";
import { VIBE_ISSUES } from "./vibe";
import { VIBE_TENANT_ALERT_ISSUES } from "./vibe-tenant-alerts";
import { WEBHOOK_ISSUES } from "./webhook.registry";
import { WHATSAPP_ISSUES } from "./whatsapp.registry";
import { WORKFLOW_DRIFT_ISSUES } from "./workflow-drift.registry";

export const ISSUE_REGISTRY = {
  ...WHATSAPP_ISSUES,
  ...TWILIO_ISSUES,
  ...SLACK_ISSUES,
  ...TEAMS_ISSUES,
  ...TELEGRAM_ISSUES,
  ...MESSENGER_ISSUES,
  ...INSTAGRAM_ISSUES,
  ...GOOGLE_ISSUES,
  ...OAUTH_ISSUES,
  ...SANDBOX_ISSUES,
  ...OUTLOOK_ISSUES,
  ...WEBHOOK_ISSUES,
  ...VIBE_ISSUES,
  ...VIBE_TENANT_ALERT_ISSUES,
  ...CLERK_ISSUES,
  ...CLERK_EVENT_OBSERVATION_ISSUES,
  ...EMBED_ISSUES,
  ...BILLING_ISSUES,
  ...WORKFLOW_DRIFT_ISSUES,
  ...POST_MERGE_CI_ISSUES,
  ...EXTERNAL_API_ISSUES,
  ...PROVIDER_SPEND_ISSUES,
  ...CAPABILITY_ISSUES
} satisfies Record<string, IssueDefinition>;

export type IssueCode = keyof typeof ISSUE_REGISTRY;

/** Whether `code` is a code the shipped registry carries — the guard for a key read as `string`. */
export function isIssueCode(code: string): code is IssueCode {
  return Object.prototype.hasOwnProperty.call(ISSUE_REGISTRY, code);
}

/** The Vibe tenant alert engine's family, by itself: every code that engine alone may retract. */
export { VIBE_TENANT_ALERT_ISSUES } from "./vibe-tenant-alerts";
